import type { ECElementEvent } from 'echarts'
import { useCallback, useMemo } from 'react'

import { EChart } from '@/components/charts/EChart'
import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { bucketKey, buildTimeSeriesOption, chartBuckets } from '@/features/report/chart-option'
import { bucketRange, isSelected } from '@/lib/cross-filter'
import { shortName, type ChartWidget } from '@/lib/cube-report'
import { messages } from '@/lib/messages'
import type { ReportRow } from '@/services/cube/report'

import { isAdditive } from './helpers'
import { WidgetCard } from './shared'
import type { ChartResult, WidgetSelection, WidgetStatus } from './types'

const NO_ROWS: ChartResult = []

/** A4 dikey içerik genişliği (180 mm ≈ 680 px); yatayda da aynı kalır, taşmaz. */
const PRINT_CHART_WIDTH = 680

/** Saf çizim: satırlar prop'tan gelir; `selection` verilmezse etkileşimsizdir. */
export function ChartWidgetView({
  widget,
  data,
  status = {},
  selection,
  printMode,
  onBreakdownChange,
}: {
  widget: ChartWidget
  data: ChartResult | undefined
  status?: WidgetStatus
  selection?: WidgetSelection
  /** Baskı: açık tema, SVG, animasyonsuz, sabit genişlik; sayfa sonunda bölünmez. */
  printMode?: boolean
  /** Verilirse `breakdownOptions` arasında seçim yapılır; `widget.breakdown` seçili olandır. */
  onBreakdownChange?: (member: string) => void
}) {
  const rows = data ?? NO_ROWS
  // Kategori ekseni: kırılımlı sorguda kova başına birden çok satır döner.
  const buckets = useMemo(() => chartBuckets(rows, widget, widget.granularity), [rows, widget])
  const member = shortName((widget.time ?? widget.category) as string)
  const selections = selection?.selections
  const onSelect = selection?.onSelect
  const keyOf = useMemo(() => bucketKey(widget, widget.granularity), [widget])
  // Seçim değeri ham satırdan okunur (`params.name` biçimli etikettir, ayrıştırılamaz):
  // zaman kovası tarih aralığı, kategori boyutun kendi değeri.
  const valueOf = useCallback(
    (row: ReportRow) => (widget.time ? bucketRange(keyOf(row), widget.granularity) : keyOf(row)),
    [widget, keyOf],
  )
  const selected = useMemo(
    () =>
      new Set(
        selections
          ? buckets
              .filter((row) => isSelected(selections, widget.key, member, valueOf(row)))
              .map(keyOf)
          : [],
      ),
    [buckets, selections, widget, member, valueOf, keyOf],
  )
  const option = useMemo(() => {
    const built = buildTimeSeriesOption(
      widget,
      rows,
      widget.granularity,
      widget.horizontal,
      selected,
    )
    return printMode ? { ...built, animation: false } : built
  }, [widget, rows, selected, printMode])
  const onClick = useMemo(
    () =>
      widget.crossFilter.emit && onSelect
        ? (params: ECElementEvent) => {
            const row = buckets[params.dataIndex]
            if (!row) return
            onSelect(
              {
                source: widget.key,
                member,
                value: valueOf(row),
                kind: widget.time ? 'range' : 'value',
              },
              isAdditive(params.event?.event as MouseEvent | undefined),
            )
          }
        : undefined,
    [widget, buckets, member, valueOf, onSelect],
  )
  const height = widget.horizontal ? Math.max(240, buckets.length * 32 + 64) : 320
  const [solid, ...striped] = widget.series.map(
    (series) => widget.fields.get(series.measure)?.label ?? shortName(series.measure),
  )

  const breakdownToggle = onBreakdownChange && widget.breakdownOptions && !printMode && (
    <ToggleGroup
      variant="outline"
      size="sm"
      aria-label={messages.report.breakdownChoice}
      value={widget.breakdown ? [widget.breakdown] : []}
      onValueChange={([value]) => {
        if (value) onBreakdownChange(value as string)
      }}
    >
      {widget.breakdownOptions.map((name) => (
        <ToggleGroupItem key={name} value={name}>
          {widget.fields.get(name)?.label ?? shortName(name)}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )

  return (
    <WidgetCard
      title={widget.title}
      busy={status.busy}
      printMode={printMode}
      actions={breakdownToggle || undefined}
    >
      <div className={printMode ? 'break-inside-avoid p-2' : 'p-2'}>
        {status.isLoading ? (
          <Loading rows={4} />
        ) : status.isError ? (
          <ErrorState error={status.error} onRetry={status.onRetry} />
        ) : rows.length === 0 ? (
          <Empty />
        ) : (
          <EChart
            option={option}
            height={height}
            notMerge
            onClick={onClick}
            renderer={printMode ? 'svg' : 'canvas'}
            light={printMode}
            width={printMode ? PRINT_CHART_WIDTH : undefined}
          />
        )}
        {widget.breakdown && solid && striped.length > 0 && rows.length > 0 && (
          <p className="text-muted-foreground px-2 pb-1 text-xs">
            {messages.report.breakdownPattern(solid, striped.join(', '))}
          </p>
        )}
      </div>
    </WidgetCard>
  )
}
